import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

// Hardcoded prototype credentials.
// NOTE: This is a client-side prototype only. Do not use for real auth.
// (Moving these to REACT_APP_* env vars would NOT hide them — CRA bundles
//  all env vars into the JS output; real security requires a backend.)
const CREDENTIALS = {
  admin: {
    email: "studio@rockstarsdigitals.com",
    password: "admin123",
    name: "Studio Admin",
    role: "admin",
  },
  client: {
    email: "client@rockstarsdigitals.com",
    password: "client123",
    name: "Client Viewer",
    role: "client",
  },
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("factor_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (user) localStorage.setItem("factor_user", JSON.stringify(user));
    else localStorage.removeItem("factor_user");
  }, [user]);

  // role = "admin" | "client"  (the tab the user has selected, used as a
  // preference only). We try the selected role first, then fall back to
  // any other role whose credentials match — this way copy-pasting the
  // client credentials while the Admin tab is still active still works.
  const login = useCallback(({ email, password, role }) => {
    const cleanEmail = (email || "").trim().toLowerCase();
    const cleanPassword = (password || "").trim();

    const roleOrder =
      role && CREDENTIALS[role]
        ? [role, ...Object.keys(CREDENTIALS).filter((r) => r !== role)]
        : Object.keys(CREDENTIALS);

    for (const r of roleOrder) {
      const expected = CREDENTIALS[r];
      if (
        cleanEmail === expected.email.toLowerCase() &&
        cleanPassword === expected.password
      ) {
        setUser({
          email: expected.email,
          name: expected.name,
          role: expected.role,
        });
        return { ok: true, role: expected.role };
      }
    }
    return { ok: false, error: "Invalid email or password" };
  }, []);

  const logout = useCallback(() => setUser(null), []);

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      isAdmin: user?.role === "admin",
      isClient: user?.role === "client",
    }),
    [user, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
export { CREDENTIALS };
